import React from "react";

import '../styles/privacy-styles.css'

function LoadPrivacyPolicy() {
    return (
        <div classNameName="container" id="privacy-policy-container">
            <div>
                <h1>Privacy Policy</h1>
                <p className="last-updated">Last Updated: November 20, 2024</p>
                
                <p className="intro">HyperWorks Imaging Pvt, Ltd. ("HyperWorks," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy describes how we collect, use, and share information when you visit our website at hyperworksimaging.com ("Site"). By using the Site, you agree to the practices described in this Privacy Policy.</p>
                
                <div className="note">
                    <strong>Product-Specific Privacy Policies:</strong> If you use any of our products or services, those products have their own privacy policies that govern data collection and usage. Please refer to the relevant product's privacy policy for detailed information about how your data is handled within that product.
                </div>

                <h2>1. Information We Collect</h2>
                
                <h3>1.1 Information You Provide to Us</h3>
                <p>We collect information that you voluntarily provide to us when you:</p>
                <ul>
                    <li><strong>Contact Us:</strong> Name, email address, phone number, company name, and the contents of your message when you submit inquiries through contact forms or email us directly</li>
                    <li><strong>Subscribe to Updates:</strong> Email address when you sign up for newsletters, product updates, or marketing communications</li>
                    <li><strong>Apply for Jobs:</strong> Resume, cover letter, contact information, and any other details you provide when applying for employment with HyperWorks Imaging</li>
                    <li><strong>Participate in Events:</strong> Name, email, phone number, and other relevant information when you register for webinars, conferences, or other events we host or attend</li>
                </ul>

                <h3>1.2 Information Collected Automatically</h3>
                <p>When you visit our Site, we automatically collect certain technical information:</p>
                <ul>
                    <li><strong>Device Information:</strong> IP address, browser type, operating system, device type, and unique device identifiers</li>
                    <li><strong>Usage Information:</strong> Pages visited, time spent on pages, links clicked, referring website, and how you navigate through our Site</li>
                    <li><strong>Cookies and Tracking Technologies:</strong> We use cookies and similar technologies to improve your experience on our Site. See Section 6 for more details on cookies</li>
                </ul>

                <hr />

                <h2>2. How We Use Your Information</h2>
                
                <p>We use the information we collect for the following purposes:</p>

                <h3>2.1 To Communicate with You</h3>
                <ul>
                    <li>Respond to your inquiries and requests for information</li>
                    <li>Send newsletters, product updates, and marketing communications (with your consent)</li>
                    <li>Provide information about our products, services, and company</li>
                    <li>Communicate about employment opportunities</li>
                </ul>

                <h3>2.2 To Improve Our Site and Services</h3>
                <ul>
                    <li>Understand how visitors use our Site</li>
                    <li>Analyze trends and gather statistical information</li>
                    <li>Improve website functionality and user experience</li>
                    <li>Develop new products and services</li>
                </ul>

                <h3>2.3 For Business Operations</h3>
                <ul>
                    <li>Process employment applications and evaluate candidates</li>
                    <li>Maintain records of communications and interactions</li>
                    <li>Manage relationships with customers, partners, and vendors</li>
                </ul>

                <h3>2.4 For Legal Compliance and Security</h3>
                <ul>
                    <li>Comply with applicable laws and regulations</li>
                    <li>Enforce our terms and policies</li>
                    <li>Protect against fraud and unauthorized access</li>
                    <li>Ensure the security of our Site and systems</li>
                </ul>

                <hr />

                <h2>3. How We Share Your Information</h2>
                
                <p>We do not sell your personal information. We may share your information only in the following circumstances:</p>

                <h3>3.1 Service Providers</h3>
                <p>We may share information with third-party service providers who assist us in operating our Site and conducting our business, including:</p>
                <ul>
                    <li>Web hosting and infrastructure providers</li>
                    <li>Email service and marketing platforms</li>
                    <li>Analytics and performance monitoring services</li>
                    <li>Customer relationship management systems</li>
                </ul>
                <p>These service providers are contractually obligated to protect your information and use it only for the specific purposes we designate.</p>

                <h3>3.2 Legal Requirements</h3>
                <p>We may disclose your information when required by law or when we believe in good faith that disclosure is necessary to:</p>
                <ul>
                    <li>Comply with legal obligations, court orders, or government requests</li>
                    <li>Enforce our terms of service and other agreements</li>
                    <li>Protect the rights, property, or safety of HyperWorks, our users, or others</li>
                    <li>Prevent or investigate fraud or security issues</li>
                </ul>

                <h3>3.3 Business Transfers</h3>
                <p>If HyperWorks is involved in a merger, acquisition, asset sale, or bankruptcy, your information may be transferred as part of that transaction. We will provide notice before your information becomes subject to a different privacy policy.</p>

                <h3>3.4 With Your Consent</h3>
                <p>We may share your information for any other purpose with your explicit consent.</p>

                <hr />

                <h2>4. Data Storage and Security</h2>

                <h3>4.1 Where We Store Your Data</h3>
                <p>HyperWorks operates primarily in India and the United States. Information we collect may be stored and processed in servers located in these countries or in other locations where we or our service providers maintain facilities.</p>

                <h3>4.2 How We Protect Your Data</h3>
                <p>We implement reasonable security measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. These measures include:</p>
                <ul>
                    <li>Secure servers and data encryption</li>
                    <li>Access controls and authentication mechanisms</li>
                    <li>Regular security assessments and monitoring</li>
                    <li>Employee training on data protection practices</li>
                </ul>
                <p>However, no method of transmission over the internet or electronic storage is completely secure. While we strive to protect your information, we cannot guarantee absolute security.</p>

                <h3>4.3 Data Retention</h3>
                <p>We retain your personal information for as long as necessary to fulfill the purposes described in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we securely delete or anonymize it.</p>

                <hr />

                <h2>5. Your Rights and Choices</h2>
                
                <p>Depending on your location, you may have certain rights regarding your personal information:</p>

                <h3>5.1 Access and Correction</h3>
                <p>You have the right to request access to your personal information and to request correction of any inaccurate or incomplete information we hold about you.</p>

                <h3>5.2 Deletion</h3>
                <p>You have the right to request deletion of your personal information, subject to certain exceptions prescribed by law (such as legal obligations or legitimate business purposes).</p>

                <h3>5.3 Marketing Communications</h3>
                <p>You can opt out of receiving marketing emails from us by:</p>
                <ul>
                    <li>Clicking the "unsubscribe" link in any marketing email</li>
                    <li>Contacting us directly at <a href="mailto:contact@hyperworksimaging.com">contact@hyperworksimaging.com</a></li>
                </ul>
                <p>Note that even if you opt out of marketing emails, we may still send you non-marketing communications related to your inquiries or our business relationship.</p>

                <h3>5.4 Exercising Your Rights</h3>
                <p>To exercise any of these rights, please contact us at:</p>
                <ul>
                    <li><strong>Email:</strong> <a href="mailto:contact@hyperworksimaging.com">contact@hyperworksimaging.com</a></li>
                    <li><strong>Data Protection Officer:</strong> <a href="mailto:naveen@hyperworksimaging.com">naveen@hyperworksimaging.com</a></li>
                </ul>
                <p>We will respond to your request within 30 days. We may need to verify your identity before processing your request.</p>

                <hr />

                <h2>6. Cookies and Tracking Technologies</h2>
                
                <p>We use cookies and similar tracking technologies to enhance your experience on our Site:</p>

                <h3>6.1 Types of Cookies We Use</h3>
                <ul>
                    <li><strong>Essential Cookies:</strong> Necessary for the Site to function properly, including security and authentication</li>
                    <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our Site (e.g., Google Analytics)</li>
                    <li><strong>Functional Cookies:</strong> Remember your preferences and enhance your experience</li>
                    <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements and track campaign effectiveness</li>
                </ul>

                <h3>6.2 Managing Cookies</h3>
                <p>You can control and manage cookies through your browser settings. Most browsers allow you to view, delete, and block cookies. However, please note that disabling certain cookies may affect the functionality of our Site.</p>

                <hr />

                <h2>7. International Data Transfers</h2>
                
                <p>HyperWorks operates in India and the United States. If you access our Site from outside these countries, your information may be transferred to, stored, and processed in these locations, where data protection laws may differ from those in your country.</p>
                <p>By using our Site, you consent to the transfer of your information to these countries. We take appropriate measures to ensure that your personal information remains protected in accordance with this Privacy Policy.</p>

                <hr />

                <h2>8. Third-Party Links</h2>
                
                <p>Our Site may contain links to third-party websites, products, or services. This Privacy Policy does not apply to these third-party sites. We are not responsible for the privacy practices or content of third parties.</p>
                <p>We encourage you to review the privacy policies of any third-party sites you visit before providing them with any information.</p>

                <hr />

                <h2>9. Children's Privacy</h2>
                
                <p>Our Site is not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you are under 18, do not use our Site or provide any information to us.</p>
                <p>If we learn that we have collected information from a person under 18, we will delete that information as quickly as possible. If you believe we may have information from someone under 18, please contact us at <a href="mailto:contact@hyperworksimaging.com">contact@hyperworksimaging.com</a>.</p>

                <hr />

                <h2>10. Changes to This Privacy Policy</h2>
                
                <p>We may update this Privacy Policy from time to time to reflect changes in our practices, technology, or legal requirements. When we make changes, we will:</p>
                <ul>
                    <li>Update the "Last Updated" date at the top of this policy</li>
                    <li>Post the updated policy on this page</li>
                    <li>Notify you via email for material changes (if you have provided us with your email address)</li>
                </ul>
                <p>We encourage you to review this Privacy Policy periodically. Your continued use of the Site after any changes constitutes your acceptance of the updated policy.</p>

                <hr />

                <div className="contact">
                    <h2>11. Contact Us</h2>
                    
                    <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
                    
                    <p><strong>HyperWorks Imaging Pvt, Ltd.</strong><br />
                    511, 6th Main, 7th Cross<br />
                    Jagadishnagar, New Tippasandra Post<br />
                    Bangalore 560075<br />
                    India</p>
                    
                    <p><strong>Email:</strong> <a href="mailto:contact@hyperworksimaging.com">contact@hyperworksimaging.com</a></p>
                    
                    <p><strong>Data Protection Officer:</strong><br />
                    Naveen Margankunte<br />
                    <a href="mailto:naveen@hyperworksimaging.com">naveen@hyperworksimaging.com</a></p>
                    
                    <p>We will respond to all inquiries within 30 days.</p>
                </div>
            </div>
        </div>
    );
}

export default LoadPrivacyPolicy;